import { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { EmptyState } from '../../components/EmptyState';
import { Panel } from '../../components/Panel';
import { toneColor } from '../../components/tone';
import { addDays, addMonths, longDate, monthGrid, monthTitle, weekStart } from '../../lib/dates';
import { TYPE_TONE, type ReminderItem } from './model';

const MODES = ['Month', 'Week', 'Day'] as const;
type Mode = (typeof MODES)[number];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Props = { items: ReminderItem[]; today: string; selectedId?: string; onSelect: (id: string) => void };

/** The month, week or day with each reminder on its date */
export function CalendarPanel({ items, today, selectedId, onSelect }: Props) {
  const phone = useMediaQuery(useTheme().breakpoints.down('sm'));
  const [anchor, setAnchor] = useState(today);
  const [mode, setMode] = useState<Mode>('Month');
  const step = (dir: -1 | 1) =>
    setAnchor((a) => (mode === 'Month' ? addMonths(a, dir) : addDays(a, dir * (mode === 'Week' ? 7 : 1))));
  const title =
    mode === 'Month'
      ? monthTitle(anchor)
      : mode === 'Week'
        ? `Week of ${longDate(weekStart(anchor))}`
        : longDate(anchor);
  const weeks =
    mode === 'Month' ? monthGrid(anchor) : [Array.from({ length: 7 }, (_v, i) => addDays(weekStart(anchor), i))];
  const on = (day: string) => items.filter((r) => r.date === day);

  return (
    <Panel title="Calendar">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={() => setAnchor(today)}>
          Today
        </Button>
        <IconButton aria-label="Previous" onClick={() => step(-1)}>
          <ChevronLeft />
        </IconButton>
        <IconButton aria-label="Next" onClick={() => step(1)}>
          <ChevronRight />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1, minWidth: 140 }}>
          {title}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={mode}
          onChange={(_e, v: Mode | null) => v && setMode(v)}
        >
          {MODES.map((m) => (
            <ToggleButton key={m} value={m} sx={{ textTransform: 'none', px: 1.75 }}>
              {m}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {mode === 'Day' ? (
        <DayList items={on(anchor)} selectedId={selectedId} onSelect={onSelect} />
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', bgcolor: 'action.hover' }}>
            {WEEKDAYS.map((d) => (
              <Typography key={d} variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 0.75 }}>
                {phone ? d[0] : d}
              </Typography>
            ))}
          </Box>
          {weeks.map((week) => (
            <Box
              key={week[0]}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              {week.map((day) => (
                <DayCell
                  key={day}
                  day={day}
                  today={today}
                  dim={mode === 'Month' && day.slice(0, 7) !== anchor.slice(0, 7)}
                  items={on(day)}
                  compact={phone}
                  selectedId={selectedId}
                  onSelect={onSelect}
                />
              ))}
            </Box>
          ))}
        </Box>
      )}
    </Panel>
  );
}

type CellProps = {
  day: string;
  today: string;
  dim: boolean;
  items: ReminderItem[];
  compact: boolean;
  selectedId?: string;
  onSelect: (id: string) => void;
};

function DayCell({ day, today, dim, items, compact, selectedId, onSelect }: CellProps) {
  const isToday = day === today;
  const shown = compact ? [] : items.slice(0, 2);
  return (
    <Box
      sx={{
        minHeight: compact ? 52 : 84,
        p: 0.5,
        borderLeft: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderLeft: 0 },
        bgcolor: isToday ? 'action.hover' : undefined,
        opacity: dim ? 0.45 : 1,
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'inline-grid',
          placeItems: 'center',
          minWidth: 22,
          height: 22,
          borderRadius: '50%',
          fontWeight: 700,
          bgcolor: isToday ? 'primary.main' : undefined,
          color: isToday ? 'primary.contrastText' : undefined,
        }}
      >
        {Number(day.slice(8))}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.25 }}>
        {shown.map((r) => (
          <EventChip key={r.id} r={r} selected={r.id === selectedId} onSelect={onSelect} />
        ))}
        {compact && items.length > 0 && (
          <Stack direction="row" spacing={0.25} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
            {items.slice(0, 3).map((r) => (
              <Box
                key={r.id}
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: toneColor(TYPE_TONE[r.type]) }}
              />
            ))}
          </Stack>
        )}
        {!compact && items.length > 2 && (
          <Typography variant="caption" color="text.secondary">
            +{items.length - 2} more
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

function EventChip({
  r,
  selected,
  onSelect,
  big,
}: {
  r: ReminderItem;
  selected: boolean;
  onSelect: (id: string) => void;
  big?: boolean;
}) {
  const color = toneColor(TYPE_TONE[r.type]);
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(r.id)}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        textAlign: 'left',
        p: big ? 1.25 : '2px 6px',
        borderRadius: 1,
        borderLeft: `3px solid ${color}`,
        bgcolor: `color-mix(in srgb, ${color} ${selected ? 26 : 12}%, transparent)`,
        color: 'text.primary',
        overflow: 'hidden',
      }}
    >
      <Typography sx={{ fontSize: big ? 14 : 11, fontWeight: 700, lineHeight: 1.25 }} noWrap>
        {r.srb || 'Reminder'}{' '}
        {r.time && (
          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
            {r.time}
          </Box>
        )}
      </Typography>
      <Typography sx={{ fontSize: big ? 13 : 11, lineHeight: 1.25 }} color="text.secondary" noWrap={!big}>
        {r.title}
      </Typography>
    </Box>
  );
}

function DayList({
  items,
  selectedId,
  onSelect,
}: {
  items: ReminderItem[];
  selectedId?: string;
  onSelect: (id: string) => void;
}) {
  if (!items.length) return <EmptyState>No reminders on this day.</EmptyState>;
  return (
    <Stack spacing={1}>
      {items.map((r) => (
        <EventChip key={r.id} r={r} selected={r.id === selectedId} onSelect={onSelect} big />
      ))}
    </Stack>
  );
}
